import React from 'react';
import { Phone, Users, Home, ClipboardCheck } from 'lucide-react';

export function AdoptionRules() {
  return (
    <section className="py-16 bg-primary/5 dark:bg-primary/20 high-contrast:bg-black">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="section-title mb-4">
            Nasze zasady adopcji
          </h2>
          <p className="body-large">
            Chcesz adoptować psa lub kota? Poznaj najważniejsze procedury naszych adopcji.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-8">
            {/* 1. Zadzwoń do nas */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
                </div>
              </div>
              <div>
                <h3 className="heading-4 mb-4">
                  1. Zadzwoń do nas
                </h3>
                <div>
                  <p className="body-medium">
                    Nie pisz, zadzwoń - to bardzo istotne! Rozmowa to ważny etap, podczas którego:
                  </p>
                  <ul className="list-disc pl-5 mt-4 space-y-2 body-medium">
                    <li className="body-medium no-margin">Opowiesz nam o warunkach, jakie możesz zapewnić</li>
                    <li className="body-medium no-margin">Pomożemy Ci wybrać zwierzaka dopasowanego do Twojego stylu życia</li>
                    <li className="body-medium no-margin">Odpowiemy na wszystkie Twoje pytania</li>
                  </ul>
                  <p className="mt-4 body-medium">
                    Jeśli nie ma podanego konkretnego numeru przy zwierzaku, zadzwoń pod nasz ogólny numer: <a href="tel:+48664821160" className="link-primary">664 821 160</a>
                  </p>
                </div>
              </div>
            </div>

            {/* 2. Osobiste spotkanie */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
                </div>
              </div>
              <div>
                <h3 className="heading-4 mb-4">
                  2. Osobiste spotkanie
                </h3>
                <div>
                  <p className="body-medium">
                    Po pozytywnej rozmowie telefonicznej zaprosimy Cię na spotkanie z wybranym zwierzakiem. Zdjęcia i opis nie oddają w pełni charakteru naszych podopiecznych, dlatego osobiste spotkanie jest tak ważne.
                  </p>
                </div>
              </div>
            </div>

            {/* 3. Wizyta przedadopcyjna */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                  <Home className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
                </div>
              </div>
              <div>
                <h3 className="heading-4 mb-4">
                  3. Wizyta przedadopcyjna
                </h3>
                <div>
                  <p className="body-medium">
                    Poprosimy Cię o przyjęcie wolontariusza na krótką wizytę przedadopcyjną. Podczas wizyty:
                  </p>
                  <ul className="list-disc pl-5 mt-4 space-y-2 body-medium">
                    <li className="body-medium no-margin">Omówimy szczepienia i żywienie</li>
                    <li className="body-medium no-margin">Sprawdzimy zabezpieczenia (np. okna, balkony)</li>
                    <li className="body-medium no-margin">Odpowiemy na wszystkie pytania</li>
                    <li className="body-medium no-margin">Doradzimy w kwestii przygotowania domu</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 4. Podpisanie umowy */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
                </div>
              </div>
              <div>
                <h3 className="heading-4 mb-4">
                  4. Podpisanie umowy
                </h3>
                <div>
                  <p className="body-medium">
                    To ostatni etap procesu adopcyjnego. W umowie zobowiązujesz się do:
                  </p>
                  <ul className="list-disc pl-5 mt-4 space-y-2 body-medium">
                    <li className="body-medium no-margin">Zapewnienia odpowiedniej opieki i miłości</li>
                    <li className="body-medium no-margin">Kastracji/sterylizacji (jeśli zwierzę nie jest jeszcze po zabiegu)</li>
                    <li className="body-medium no-margin">Natychmiastowego powiadomienia nas w razie zaginięcia</li>
                    <li className="body-medium no-margin">Nieodsprzedawania ani nieoddawania zwierzęcia bez naszej wiedzy</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Ważne informacje */}
          <div className="bg-accessible-gray-50 dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg">
            <h3 className="heading-4 mb-6">
              Ważne informacje
            </h3>
            <div className="space-y-6">
              <div>
                <h4 className="heading-5 mb-2">
                  Adopcja kotów
                </h4>
                <p className="body-medium">
                  Wszystkie nasze koty są przeznaczone do mieszkania w domu. Wymagamy zabezpieczenia okien i balkonów. 
                  Nie wydajemy kotów na dwór.
                </p>
              </div>
              <div>
                <h4 className="heading-5 mb-2">
                  Adopcja psów
                </h4>
                <p className="body-medium">
                  Nie wydajemy psów na łańcuch ani do budy. Pies musi mieć zapewnione odpowiednie schronienie 
                  i możliwość przebywania w domu z rodziną.
                </p>
              </div>
              <div>
                <h4 className="heading-5 mb-2">
                  Ankieta przedadopcyjna
                </h4>
                <p className="body-medium">
                  Nie wymagamy wypełniania ankiety przedadopcyjnej. Najważniejsza jest dla nas rozmowa telefoniczna 
                  i osobiste spotkanie.
                </p>
              </div>
              <div>
                <h4 className="heading-5 mb-2">
                  Czas na decyzję
                </h4>
                <p className="body-medium">
                  Nie wymagamy podjęcia decyzji od razu. Możesz odwiedzić zwierzaka kilka razy, aby lepiej się poznać 
                  i upewnić w swojej decyzji.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
